import React, { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MessageCircle, Send, X, Loader2 } from 'lucide-react';
import axios from 'axios';

const RideChat = ({ 
  rideId, 
  senderId, 
  senderType, // 'driver' or 'user'
  senderName,
  recipientName,
  backendUrl,
  onClose,
  isOpen = true,
  newMessage = null // From WebSocket
}) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const messagesEndRef = useRef(null);

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch existing messages
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/messages/ride/${rideId}`);
        setMessages(response.data.messages || []);
      } catch (error) {
        console.error('Error fetching messages:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (rideId) {
      fetchMessages();
    }
  }, [rideId, backendUrl]);

  // Handle incoming WebSocket messages
  useEffect(() => {
    if (newMessage && newMessage.ride_id === rideId) {
      setMessages(prev => {
        // Avoid duplicates
        if (prev.some(m => m.id === newMessage.id)) return prev;
        return [...prev, newMessage];
      });
    }
  }, [newMessage, rideId]);

  // Scroll on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!inputMessage.trim()) return;

    setIsSending(true);
    try {
      const response = await axios.post(`${backendUrl}/api/messages/send`, {
        ride_id: rideId,
        sender_type: senderType,
        sender_id: senderId,
        message: inputMessage.trim()
      });

      // Add to local messages immediately
      if (response.data.message) {
        setMessages(prev => [...prev, response.data.message]);
      }
      setInputMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <Card className="flex flex-col h-80 border-2 border-amber-300 shadow-lg" data-testid="ride-chat">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-gradient-to-r from-amber-50 to-yellow-50">
        <div className="flex items-center gap-2">
          <MessageCircle size={18} className="text-amber-600" />
          <span className="font-semibold text-sm">Chat with {recipientName}</span>
        </div>
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0">
            <X size={14} />
          </Button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="animate-spin text-amber-500" size={24} />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((msg, index) => {
            const isOwnMessage = msg.sender_type === senderType && msg.sender_id === senderId;
            return (
              <div
                key={msg.id || index}
                className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[75%] px-3 py-2 rounded-lg text-sm ${
                    isOwnMessage
                      ? 'bg-amber-500 text-white rounded-br-none'
                      : 'bg-white text-gray-800 rounded-bl-none border shadow-sm'
                  }`}
                >
                  {!isOwnMessage && (
                    <p className="text-xs font-medium text-gray-500 mb-1">{msg.sender_name}</p>
                  )}
                  <p className="break-words">{msg.message}</p>
                  <p className={`text-xs mt-1 ${isOwnMessage ? 'text-amber-100' : 'text-gray-400'}`}>
                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-2 border-t bg-white">
        <div className="flex gap-2">
          <Input
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            className="flex-1 text-sm"
            disabled={isSending}
            data-testid="chat-input"
          />
          <Button
            onClick={handleSend}
            disabled={!inputMessage.trim() || isSending}
            className="bg-amber-500 hover:bg-amber-600"
            size="sm"
            data-testid="chat-send-btn"
          >
            {isSending ? (
              <Loader2 className="animate-spin" size={16} />
            ) : (
              <Send size={16} />
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default RideChat;
